import React from "react";

// Matches http(s) URLs. Trailing sentence punctuation is trimmed off the match
// below so "see https://x.com." doesn't capture the period as part of the link.
const URL_REGEX = /(https?:\/\/[^\s<]+)/g;
const TRAILING_PUNCT = /[.,;:!?)\]}'"]+$/;

/**
 * Render free text with http(s) URLs turned into safe, clickable links.
 *
 * Security: only http/https URLs are linkified; everything else is emitted as
 * plain React text nodes (auto-escaped by React), so this is XSS-safe — no
 * `dangerouslySetInnerHTML`. Links open in a new tab with `rel="noopener
 * noreferrer"` so the target can't reach `window.opener`.
 */
export function linkifyText(text: string): React.ReactNode {
  if (!text) return text;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(text)) !== null) {
    const raw = match[0];
    // Trim trailing punctuation that belongs to the surrounding prose, not the URL.
    const trimmed = raw.replace(TRAILING_PUNCT, "");
    const start = match.index;

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    nodes.push(
      <a
        key={`link-${key++}`}
        href={trimmed}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 dark:text-blue-400 underline underline-offset-2 hover:text-blue-700 dark:hover:text-blue-300 break-all"
        onClick={(e) => e.stopPropagation()}
      >
        {trimmed}
      </a>,
    );

    // Emit any punctuation we trimmed as plain text so it isn't lost.
    const trailing = raw.slice(trimmed.length);
    if (trailing) nodes.push(trailing);

    lastIndex = start + raw.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}
